/**
 * @fileoverview Staff a program — the "+ Invite instructors" popup.
 *
 * Two lists, in the order an org actually thinks:
 *
 * 1. **People you've worked with.** Staffing a new program is usually
 *    re-staffing, so instructors who teach (or used to teach) in this org's
 *    programs come first, with the programs they taught. Someone already on
 *    this program is shown and disabled rather than hidden, so you can see
 *    they are handled instead of wondering where they went.
 * 2. **Anyone else, by email.** If they are on Hans the invitation lands in
 *    their account and their details fill in when they accept; if they are
 *    not, it invites them to Hans first. Either way the org never sees an
 *    email address it did not already have.
 *
 * Below those sits the wider Hans directory — instructors who opted in to
 * being found for work — and the invitations already out, which can be
 * withdrawn while nobody has answered them. All of it posts to the same
 * endpoint, so one invitation record covers every route in.
 *
 * An invitation can also NAME THE CLASSES the recipient is being invited to
 * teach. Staffing a term is deciding who takes which class, so the invitation
 * carries that decision and the classes are assigned the moment it is
 * accepted — rather than making the admin come back and repeat it once the
 * acceptance lands. Only classes nobody is on yet can be offered: promising
 * one that is already taught would be a promise the acceptance refuses.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Button, Icon, Tag } from "@/components/studio/primitives";

const INVITE_STATUS = {
  pending: "Waiting on them",
  accepted: "Accepted",
  declined: "Declined",
  revoked: "Withdrawn",
};

const inputClass =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[10px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";

export default function InviteInstructorsDialog({
  open,
  onOpenChange,
  programId,
  unassignedClasses = [],
  onInvited,
}) {
  const [affiliated, setAffiliated] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [invites, setInvites] = useState([]);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(() => new Set());
  const [emails, setEmails] = useState("");
  const [classes, setClasses] = useState(() => new Set());
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const [mine, open, sent] = await Promise.all([
        orgApi.get(`/org/affiliated-instructors?program_id=${encodeURIComponent(programId)}`),
        orgApi.get("/org/instructor-directory").catch(() => ({ data: { instructors: [] } })),
        orgApi.get(`/org/programs/${programId}/invites`).catch(() => ({ data: { invites: [] } })),
      ]);
      setAffiliated(mine.data?.instructors || []);
      setInvites(sent.data?.invites || []);
      // Anyone already in the org list is not a stranger — showing them twice
      // would let one person be picked from two places.
      const known = new Set((mine.data?.instructors || []).map((i) => i.user_id));
      setDirectory((open.data?.instructors || []).filter((i) => !known.has(i.user_id)));
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't load your instructors"));
    } finally {
      setLoading(false);
    }
  }, [open, programId]);

  useEffect(() => { load(); }, [load]);

  // Reset between openings so a dialog never reopens holding a stale selection.
  useEffect(() => {
    if (!open) {
      setPicked(new Set());
      setClasses(new Set());
      setEmails("");
      setMessage("");
      setQuery("");
    }
  }, [open]);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return affiliated;
    return affiliated.filter(
      (i) =>
        i.name.toLowerCase().includes(needle) ||
        (i.instruments || []).some((s) => String(s).toLowerCase().includes(needle)),
    );
  }, [affiliated, query]);

  const toggle = (userId) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });

  const revoke = async (inviteId) => {
    try {
      await orgApi.delete(`/org/programs/${programId}/invites/${inviteId}`);
      toast.success("Invitation withdrawn");
      load();
      onInvited?.();
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't withdraw that invitation"));
    }
  };

  /* Someone with an invitation already out must not be invited again — the
     endpoint would skip them, but offering the click at all is a lie.

     PENDING only. An ACCEPTED invitation means they answered: either they are
     on the program now, which `already_on_program` says, or the membership has
     since ended and re-inviting them is exactly what this dialog is for.
     Counting accepted here left a removed instructor greyed out for good. */
  const alreadyInvited = useMemo(
    () =>
      new Set(
        invites
          .filter((i) => i.status === "pending")
          .map((i) => i.user_id)
          .filter(Boolean),
      ),
    [invites],
  );

  const typedEmails = emails.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);

  const send = async () => {
    if (picked.size === 0 && typedEmails.length === 0) {
      toast.error("Pick someone, or enter an email address");
      return;
    }
    setSending(true);
    try {
      const res = await orgApi.post(`/org/programs/${programId}/invites`, {
        user_ids: [...picked],
        emails: typedEmails,
        class_ids: [...classes],
        message: message.trim() || undefined,
      });
      const { sent = 0, skipped = 0 } = res.data || {};
      toast.success(
        sent === 0
          ? "Everyone you picked was already invited"
          : `${sent} invitation${sent === 1 ? "" : "s"} sent${skipped ? ` · ${skipped} skipped` : ""}` +
            (classes.size ? ` · ${classes.size} class${classes.size === 1 ? "" : "es"} on accept` : ""),
      );
      onInvited?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't send the invitations"));
    } finally {
      setSending(false);
    }
  };

  const programSummary = (i) => {
    const now = i.current_programs?.length || 0;
    const before = i.past_programs?.length || 0;
    if (now) return `Teaching ${now} of your program${now === 1 ? "" : "s"}`;
    if (before) return `Taught ${i.past_programs.map((p) => p.title).slice(0, 2).join(", ")}`;
    return "";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl font-semibold">Invite instructors</DialogTitle>
        </DialogHeader>

        <div className="mt-2">
          <div className="mb-2 font-sans text-[13px] font-medium text-[var(--text-tertiary)]">
            People you&rsquo;ve worked with
          </div>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or instrument"
            aria-label="Search your instructors"
            data-testid="invite-affiliated-search"
            className={inputClass}
          />

          <div className="mt-2 flex flex-col gap-1">
            {loading ? (
              <p className="py-2 font-sans text-[14px] text-[var(--text-tertiary)]">Loading…</p>
            ) : shown.length === 0 ? (
              <p className="py-2 font-sans text-[14px] text-[var(--text-tertiary)]">
                {affiliated.length === 0
                  ? "Nobody has taught in your programs yet — invite someone by email below."
                  : "Nobody matches that search."}
              </p>
            ) : (
              shown.map((i) => (
                <button
                  key={i.user_id}
                  type="button"
                  disabled={i.already_on_program || alreadyInvited.has(i.user_id)}
                  onClick={() => toggle(i.user_id)}
                  data-testid={`invite-affiliated-${i.user_id}`}
                  className={`flex items-center justify-between gap-3 rounded-[12px] px-3 py-2 text-left transition-colors disabled:opacity-50 ${
                    picked.has(i.user_id) ? "bg-[var(--accent-yellow-dim)]" : "hover:bg-[var(--studio-interactive)]"
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block font-sans text-[15px] text-[var(--text-primary)]">{i.name}</span>
                    <span className="block font-sans text-[13px] text-[var(--text-tertiary)]">
                      {programSummary(i)}
                    </span>
                  </span>
                  {i.already_on_program ? (
                    <Tag>Already here</Tag>
                  ) : alreadyInvited.has(i.user_id) ? (
                    <Tag>Invited</Tag>
                  ) : picked.has(i.user_id) ? (
                    <Icon name="check" size={17} />
                  ) : null}
                </button>
              ))
            )}
          </div>
        </div>

        <div className="mt-5">
          <div className="mb-2 font-sans text-[13px] font-medium text-[var(--text-tertiary)]">
            Open to work on Hans
          </div>
          {directory.length === 0 ? (
            <p data-testid="directory-empty" className="font-sans text-[13px] text-[var(--text-tertiary)]">
              No instructors are open to work yet. Instructors choose to be listed in their own settings — you can
              still invite anyone by email below.
            </p>
          ) : (
            <div className="flex flex-col gap-1">
              {directory.slice(0, 8).map((i) => (
                <button
                  key={i.user_id}
                  type="button"
                  disabled={alreadyInvited.has(i.user_id)}
                  onClick={() => toggle(i.user_id)}
                  data-testid={`directory-row-${i.user_id}`}
                  className={`flex items-center justify-between gap-3 rounded-[12px] px-3 py-2 text-left transition-colors disabled:opacity-50 ${
                    picked.has(i.user_id) ? "bg-[var(--accent-yellow-dim)]" : "hover:bg-[var(--studio-interactive)]"
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block font-sans text-[15px] text-[var(--text-primary)]">
                      {i.name}
                      {alreadyInvited.has(i.user_id) && (
                        <span className="ml-2 text-[12px] text-[var(--text-tertiary)]">Already invited</span>
                      )}
                    </span>
                    <span className="block font-sans text-[13px] text-[var(--text-tertiary)]">
                      {[
                        (i.instruments || []).join(", "),
                        [i.city, i.state].filter(Boolean).join(", "),
                        i.years_experience != null ? `${i.years_experience} yrs teaching` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </span>
                  {picked.has(i.user_id) && <Icon name="check" size={17} />}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="mt-5">
          <div className="mb-2 font-sans text-[13px] font-medium text-[var(--text-tertiary)]">Or invite by email</div>
          <textarea
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            placeholder={"teacher@email.com — one per line or comma-separated"}
            rows={2}
            aria-label="Invite by email"
            data-testid="invite-emails"
            className={inputClass}
          />
          <p className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">
            Already on Hans? The invitation lands in their account and their details fill in when they accept.
            Not yet? They&rsquo;ll be invited to Hans first.
          </p>
        </div>

        {unassignedClasses.length > 0 && (
          <div className="mt-5">
            <div className="mb-2 font-sans text-[13px] font-medium text-[var(--text-tertiary)]">
              Classes they&rsquo;ll take on
            </div>
            <div className="flex flex-col gap-1">
              {unassignedClasses.map((c) => (
                <button
                  key={c.class_id}
                  type="button"
                  onClick={() =>
                    setClasses((prev) => {
                      const next = new Set(prev);
                      if (next.has(c.class_id)) next.delete(c.class_id);
                      else next.add(c.class_id);
                      return next;
                    })
                  }
                  data-testid={`invite-class-${c.class_id}`}
                  className={`flex items-center justify-between gap-3 rounded-[12px] px-3 py-2 text-left transition-colors ${
                    classes.has(c.class_id)
                      ? "bg-[var(--accent-yellow-dim)]"
                      : "hover:bg-[var(--studio-interactive)]"
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block font-sans text-[15px] text-[var(--text-primary)]">{c.name}</span>
                    <span className="block font-sans text-[13px] text-[var(--text-tertiary)]">
                      Nobody assigned yet
                    </span>
                  </span>
                  {classes.has(c.class_id) && <Icon name="check" size={17} />}
                </button>
              ))}
            </div>
            <p className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">
              Assigned the moment they accept. If you staff one another way in the meantime, it stays where you
              put it.
            </p>
          </div>
        )}

        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Optional note to include"
          rows={2}
          aria-label="Optional note"
          data-testid="invite-message"
          className={`${inputClass} mt-3`}
        />

        {invites.length > 0 && (
          <div className="mt-5">
            <div className="mb-2 font-sans text-[13px] font-medium text-[var(--text-tertiary)]">
              Already invited
            </div>
            <div className="flex flex-col gap-1">
              {invites.map((i) => (
                <div key={i.invite_id} data-testid={`sent-invite-${i.invite_id}`} className="flex items-center justify-between gap-3 px-3 py-1.5">
                  <span className="min-w-0 font-sans text-[14px] text-[var(--text-secondary)]">
                    {i.name || i.email}
                  </span>
                  <span className="flex shrink-0 items-center gap-2">
                    <Tag>{INVITE_STATUS[i.status] || i.status}</Tag>
                    {/* Only an unanswered invitation can be withdrawn — taking
                        back one already accepted would be a removal, which
                        belongs on the instructor list. */}
                    {i.status === "pending" && (
                      <button
                        type="button"
                        onClick={() => revoke(i.invite_id)}
                        data-testid={`revoke-${i.invite_id}`}
                        className="font-sans text-[13px] text-[var(--state-danger)] hover:underline"
                      >
                        Withdraw
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 flex items-center gap-3">
          <Button onPress={send} disabled={sending} data-testid="invite-send">
            {sending
              ? "Sending…"
              : `Send ${picked.size + typedEmails.length || ""} invitation${picked.size + typedEmails.length === 1 ? "" : "s"}`.replace("  ", " ")}
          </Button>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="font-sans text-[14px] text-[var(--text-tertiary)] hover:underline"
          >
            Cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
